import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ImageIcon from '@mui/icons-material/Image';
import ListItemIcon from '@mui/material/AccordionDetails';
import Avatar from '@mui/material/Avatar';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import {styled} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, {useState, useEffect} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

import Images from '../../constants/images';
import NavigationListConstant from '../../constants/navigationLists';
import {pxToRem} from '../../utils';

const StyledWrapperAside = styled('aside')(({theme}) => ({
  width: 200,
  flex: `0 0 200px`,
  backgroundColor: theme.palette.primary.light2,
  height: '100vh',
  position: 'fixed',
  zIndex: 10,

  '& .MuiPaper-root-MuiAccordion-root:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: 0,
  },
  '& .MuiPaper-root-MuiAccordion-root.Mui-expanded': {
    margin: 0,
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
  },
}));

const StyledWrapperDiv = styled('div')(({theme}) => ({
  left: 0,
  top: 0,
  boxShadow: theme.palette.boxShadow.type4,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: theme.palette.white,
}));

const StyledListDiv = styled('div')(({theme}) => ({
  backgroundColor: 'inherit',
  border: '0',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,

  width: '100%',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

const StyledTopImageDiv = styled('div')(({theme}) => ({
  backgroundColor: theme.palette.grey['100'],
  width: pxToRem(180),
  borderRadius: pxToRem(8),
  marginTop: pxToRem(8),
  marginBottom: pxToRem(32),
  marginLeft: pxToRem(9.6),
}));

const StyledEmptyListIcon = styled('img')(({theme}) => ({
  width: 105,
  height: 105,
  marginLeft: pxToRem(33),
}));

const StyledSideBarListDiv = styled('div')(({theme}) => ({
  height: `calc(100vh - 160px)`,
  overflowX: 'hidden',
  overflowY: 'auto',
}));

const StyledAvatar = styled(Avatar)(({theme}) => ({
  height: pxToRem(20),
  width: pxToRem(20),
  textAlign: 'center',
  margin: theme.spacing(1),
  backgroundColor: 'inherit',
  borderRadius: pxToRem(0),
  color: theme.palette.primary.main,
}));

const StyledTypography = styled(Typography)(({theme, isselected}) => ({
  fontSize: isselected ? pxToRem(12) : pxToRem(11.5),
  fontWeight: 800,
  color: theme.palette.primary.main,
  marginRight: isselected ? theme.spacing(-1) : theme.spacing(-3),
}));

const StyledListItemButton = styled(ListItemButton)(({theme, isnested, isnesteditem, isselecteditem}) => ({
  opacity: isselecteditem === 'true' ? 1 : 0.5,
  marginLeft: isnesteditem === 'true' ? pxToRem(16) : '0',
  '& .MuiAccordionDetails-root': {
    padding: 0,
  },
  backgroundColor:
    isselecteditem === 'true' && isnested === 'false' ? theme.palette.primary.light2 : theme.palette.white,
  '&:hover': {
    backgroundColor:
      isselecteditem === 'true' && isnested === 'false' ? theme.palette.primary.light2 : theme.palette.white,
  },
  ...(isselecteditem === 'true' &&
    isnested === 'false' && {
      '&:after': {
        content: "''",
        height: '70%',
        width: 5.3,
        backgroundColor: theme.palette.primary.main,
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        marginTop: 'auto',
        marginBottom: 'auto',
        borderRadius: 3.5,
      },
    }),
}));

const SideBarListItem = ({item, handleClick, isNested, isNestedItem, isSelectedItem}) => {
  const [open, setOpen] = useState(true);
  const handleOpen = (item, isNested) => {
    if (isNested) {
      setOpen((prevState) => !prevState);
    } else {
      handleClick(item);
    }
  };
  return (
    <>
      <StyledListItemButton
        isnested={`${isNested}`}
        isnesteditem={`${isNestedItem}`}
        isselecteditem={`${isSelectedItem}`}
        onClick={() => handleOpen(item, isNested)}
      >
        <ListItemIcon>
          <StyledAvatar>{item.icon ? <item.icon /> : <ImageIcon />}</StyledAvatar>
        </ListItemIcon>
        <ListItemText>
          <StyledTypography isselected={isSelectedItem?.toString()}>{item.label}</StyledTypography>
        </ListItemText>
        {Array.isArray(item?.subArray) && item?.subArray.length > 0 ? (
          open ? (
            <ExpandLess sx={{color: (theme) => theme.palette.primary.dark}} />
          ) : (
            <ExpandMore sx={{color: (theme) => theme.palette.primary.dark}} />
          )
        ) : (
          <></>
        )}
      </StyledListItemButton>
      {isNested ? (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding dense={true}>
            {item?.subArray?.map((item, index) => {
              return (
                <SideBarListItem
                  key={index}
                  item={item}
                  isNested={false}
                  isNestedItem={true}
                  isSelectedItem={item?.isSelected}
                  handleClick={handleClick}
                />
              );
            })}
          </List>
        </Collapse>
      ) : (
        <></>
      )}
    </>
  );
};

function Sidebar({finalNavBarList}) {
  const history = useHistory();
  const location = useLocation();
  const [navBarList, setNavBarList] = useState(finalNavBarList);

  useEffect(() => {
    const currentPath = location.pathname;
    const updatedList = finalNavBarList.map((item) => {
      if (item?.link === currentPath || item?.subArray?.some((subItem) => subItem?.link === currentPath)) {
        if (item?.subArray) {
          const updatedSubArray = item?.subArray?.map((subItem) => {
            return {...subItem, isSelected: subItem.link === currentPath};
          });
          return {...item, isSelected: true, subArray: updatedSubArray};
        }
        return {...item, isSelected: true};
      }
      return item;
    });
    setNavBarList(updatedList);
  }, [finalNavBarList, location]);

  const handleClick = (item) => {
    if (item?.link) {
      history.push(item.link);
    }
  };

  return (
    <StyledWrapperAside>
      <StyledWrapperDiv>
        <StyledListDiv key={NavigationListConstant}>
          <StyledTopImageDiv>
            <StyledEmptyListIcon src={Images.hubLogo} alt="hubLogo" />
          </StyledTopImageDiv>
          <StyledSideBarListDiv>
            <List dense={true}>
              {navBarList?.map((item, index) => {
                return (
                  <SideBarListItem
                    key={index}
                    item={item}
                    handleClick={handleClick}
                    isNested={Array.isArray(item?.subArray) && item.subArray.length > 0}
                    isNestedItem={false}
                    isSelectedItem={item.isSelected}
                  />
                );
              })}
            </List>
          </StyledSideBarListDiv>
        </StyledListDiv>
      </StyledWrapperDiv>
    </StyledWrapperAside>
  );
}

export default Sidebar;
